import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

// 사용자 타입 정의
interface User {
  id: string;
  email: string;
  name: string;
  userType: 'hospital_admin' | 'dept_admin' | 'nurse';
  hospitalId?: string;  // 병원 ID (병원 관리자, 부서 관리자용)
  departmentId?: string; // 부서 ID (부서 관리자용)
}

// 병원 타입 정의
interface Hospital {
  id: string;
  name: string;
  adminId?: string;
  // 기타 필요한 필드
}

// 인증 컨텍스트 타입 정의
interface AuthContextType {
  hospital: Hospital | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<{ user: any; hospital: any }>;
  logout: () => void;
  updateProfile: (hospitalData: any) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// 기본 컨텍스트 값 설정
const AuthContext = createContext<AuthContextType>({
  hospital: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => {},
  register: async () => ({ user: null, hospital: null }),
  logout: () => {},
  updateProfile: async () => {},
  changePassword: async () => {},
});

// 개발 환경에서만 사용하는 테스트 계정 (환경변수 기반)
const getTestCredentials = () => {
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_TEST_MODE === 'true') {
    return {
      email: process.env.REACT_APP_TEST_EMAIL || '',
      password: process.env.REACT_APP_TEST_PASSWORD || '',
      token: process.env.REACT_APP_TEST_TOKEN || 'dev-test-token'
    };
  }
  return null;
};

// 테스트 병원 정보 (개발 환경에서만)
const getTestHospitalData = () => {
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_TEST_MODE === 'true') {
    return {
      id: 'test-hospital-id',
      name: '메디브릿지 종합병원',
      contactEmail: 'contact@medibridge.example.com',
      contactPhone: '02-1234-5678',
      address: '서울시 강남구 테헤란로 123',
      phoneNumber: '02-1234-5678',
      description: '테스트 목적으로 사용되는 병원입니다. 현재 백엔드 연결 없이 로컬에서만 작동합니다.',
      logoUrl: '/logo192.png',
      websiteUrl: 'https://medibridge.example.com',
      city: 'Seoul',
      state: 'Gangnam',
      postalCode: '06234',
      latitude: 37.5665,
      longitude: 126.9780,
      rating: 4.5,
      reviewCount: 120,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }
  return null;
};

// AuthProvider 컴포넌트 생성
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 인증 오류 이벤트 처리
  useEffect(() => {
    const handleUnauthorized = () => {
      // 토큰 제거 및 상태 업데이트
      localStorage.removeItem('token');
      localStorage.removeItem('hospitalId');
      setHospital(null);
      setIsAuthenticated(false);
      console.log('인증 만료: 로그인이 필요합니다');
    };
    
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  // 토큰 확인 및 프로필 로드
  useEffect(() => {
    const loadUserProfile = async () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken'); // 리프레시 토큰도 확인
      
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        // 개발 환경 테스트 계정 확인
        const testCredentials = getTestCredentials();
        const testHospitalData = getTestHospitalData();
        
        if (testCredentials && testHospitalData && token === testCredentials.token) {
          setHospital(testHospitalData);
          setIsAuthenticated(true);
        } else {
          // 실제 API 호출 (백엔드 연결 시 사용)
          try {
            const response = await api.get('/auth/me');
            console.log('🔍 Me response full:', response.data); // For debugging
            
            // 응답 구조 처리 - 백엔드는 { success: true, data: { user, hospital } } 구조
            const responseData = response.data.data || response.data;
            
            if (responseData.user) {
              console.log('👤 User from /auth/me:', responseData.user);
              console.log('🏥 Hospital from /auth/me:', responseData.hospital);
              
              let userWithHospitalId = { ...responseData.user };
              
              // hospital 정보가 있으면 user 객체에 hospitalId 추가
              if (responseData.hospital) {
                userWithHospitalId.hospitalId = responseData.hospital.id;
                setHospital(responseData.hospital);
                localStorage.setItem('hospitalId', responseData.hospital.id);
              } else {
                // hospital 정보가 없으면 user 정보를 hospital로 사용 (기존 호환성)
                setHospital(responseData.user);
                if (responseData.user.id) {
                  localStorage.setItem('hospitalId', responseData.user.id);
                  userWithHospitalId.hospitalId = responseData.user.id;
                }
              }
              
              console.log('💾 Saving user data on init:', userWithHospitalId);
              // user 정보 설정 (hospitalId 포함)
              setUser(userWithHospitalId);
              // ✅ localStorage에 user 정보 저장 (ChatRoom에서 사용)
              localStorage.setItem('user', JSON.stringify(userWithHospitalId));
              setIsAuthenticated(true);
            }
          } catch (profileError) {
            // 토큰이 만료되었거나 유효하지 않은 경우, 리프레시 토큰으로 갱신 시도
            if (refreshToken) {
              try {
                const refreshResponse = await api.post('/auth/refresh-token', { refreshToken });
                const newToken = refreshResponse.data.accessToken;
                
                if (newToken) {
                  localStorage.setItem('token', newToken);
                  // 새 토큰으로 사용자 정보 다시 요청
                  const meResponse = await api.get('/auth/me');
                  const userData = meResponse.data.data || meResponse.data;
                  setHospital(userData);
                  setIsAuthenticated(true);
                  
                  if (userData && userData.id) {
                    localStorage.setItem('hospitalId', userData.id);
                  }
                } else {
                  throw new Error('Failed to refresh token');
                }
              } catch (refreshError) {
                // 리프레시 토큰도 만료된 경우 로그아웃 처리
                console.error('Failed to refresh token:', refreshError);
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                setIsAuthenticated(false);
              }
            } else {
              // 리프레시 토큰이 없는 경우 로그아웃 처리
              console.error('Failed to load profile and no refresh token:', profileError);
              localStorage.removeItem('token');
              setIsAuthenticated(false);
            }
          }
        }
      } catch (err: any) {
        console.error('Failed to load profile:', err);
        localStorage.removeItem('token');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  // 로그인 함수
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 입력값 정규화
      const normalizedEmail = email.trim().toLowerCase();
      const normalizedPassword = password.trim();
      
      console.log('🔍 Frontend login attempt:', { 
        originalEmail: email,
        normalizedEmail,
        originalPasswordLength: password.length,
        trimmedPasswordLength: normalizedPassword.length,
        hasEmailSpaces: email !== email.trim(),
        hasPasswordSpaces: password !== password.trim()
      });
      
      // 개발 환경 테스트 계정 확인
      const testCredentials = getTestCredentials();
      const testHospitalData = getTestHospitalData();
      
      if (testCredentials && normalizedEmail === testCredentials.email && normalizedPassword === testCredentials.password) {
        // 테스트 계정으로 로그인 성공
        localStorage.setItem('token', testCredentials.token);
        localStorage.setItem('hospitalId', testHospitalData!.id);
        setHospital(testHospitalData);
        setIsAuthenticated(true);
      } else {
        // 실제 API 호출
        const response = await api.post('/auth/login', { 
          email: normalizedEmail, 
          password: normalizedPassword 
        });
        console.log('🔐 Login response full:', response.data); // For debugging
        
        // 서버 응답 구조에 따라 데이터 추출
        // 응답이 { success: true, data: { accessToken, user } } 구조인 경우
        const accessToken = response.data.data?.accessToken || response.data.accessToken;
        const refreshToken = response.data.data?.refreshToken || response.data.refreshToken;
        const user = response.data.data?.user || response.data.user;
        const hospital = response.data.data?.hospital || response.data.hospital;
        
        // 토큰 저장
        localStorage.setItem('token', accessToken);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
        
        // 사용자 정보 설정
        if (user) {
          console.log('👤 User object from login:', user);
          console.log('🏥 Hospital object from login:', hospital);
          
          let userWithHospitalId = { ...user };
          
          // 병원 정보가 있으면 user 객체에 hospitalId 추가
          if (hospital && hospital.id) {
            userWithHospitalId.hospitalId = hospital.id;
            localStorage.setItem('hospitalId', hospital.id);
          } else if (user.userType === 'hospital_admin') {
            // 병원 관리자인데 hospital 정보가 없는 경우, user.id를 hospitalId로 사용
            userWithHospitalId.hospitalId = user.id;
            localStorage.setItem('hospitalId', user.id);
          }
          
          console.log('💾 Saving user to localStorage:', userWithHospitalId);
          setUser(userWithHospitalId);
          // ✅ localStorage에 user 정보 저장 (ChatRoom에서 사용)
          localStorage.setItem('user', JSON.stringify(userWithHospitalId));
        }
        setHospital(hospital || user); // 병원 정보가 있으면 사용, 없으면 user로 대체
        
        // 사용자 ID 저장
        if (user && user.id) {
          localStorage.setItem('userId', user.id);
        }
        
        // 부서 관리자인 경우 추가 처리
        if (user && user.userType === 'dept_admin' && !hospital) {
          // 부서담당자인 경우 관리하는 부서의 병원 ID를 조회
          try {
            console.log('부서관리자 로그인: 병원 ID 조회 시작');
            const departmentResponse = await api.get(`/departments`);
            const departments = departmentResponse.data?.departments || [];
            const userDepartment = departments.find((dept: any) => dept.adminId === user.id);
            if (userDepartment && userDepartment.hospitalId) {
              localStorage.setItem('hospitalId', userDepartment.hospitalId);
              console.log('부서관리자 병원 ID 설정 완료:', userDepartment.hospitalId);
            } else {
              console.warn('부서관리자의 병원 정보를 찾을 수 없습니다.');
            }
          } catch (deptError) {
            console.error('Failed to get department hospital ID:', deptError);
          }
        } else if (user && user.userType === 'hospital_admin') {
          // 병원관리자인데 병원 정보가 없는 경우 자동 생성될 수 있으므로 잠시 대기 후 다시 시도
          console.log('병원관리자 로그인: 병원 정보 재조회 시작');
          try {
            // 잠시 대기 후 재시도 (병원 자동 생성 대기)
            setTimeout(async () => {
              try {
                const userInfoResponse = await api.get('/auth/me');
                if (userInfoResponse.data?.hospital?.id) {
                  localStorage.setItem('hospitalId', userInfoResponse.data.hospital.id);
                  console.log('병원관리자 병원 ID 재조회 완료:', userInfoResponse.data.hospital.id);
                }
              } catch (retryError) {
                console.error('병원 정보 재조회 실패:', retryError);
              }
            }, 1000);
          } catch (retryError) {
            console.error('병원 정보 재조회 실패:', retryError);
          }
        }
        
        setIsAuthenticated(true);
      }
    } catch (err: any) {
      setError(err.message || '로그인에 실패했습니다.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 회원가입 함수
  const register = async (userData: any) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 필요한 데이터 준비
      const registrationData = {
        email: userData.email,
        password: userData.password,
        name: userData.name,
        phone: userData.phoneNumber,
        userType: userData.userType || 'hospital_admin', // 부서 관리자 생성 지원
        // 추가 데이터
        address: userData.address,
        description: userData.description
      };
      
      // 실제 API 호출
      const response = await api.post('/auth/register', registrationData);
      console.log('Register response:', response.data); // For debugging
      
      // 응답에서 사용자 정보 추출
      const user = response.data.data?.user || response.data.user;
      const hospital = response.data.data?.hospital || response.data.hospital;
      
      // 병원관리자(hospital_admin) 생성인 경우에만 로그인 상태 변경
      if (userData.userType === 'hospital_admin' || !userData.userType) {
        // 응답에서 토큰 추출
        const accessToken = response.data.data?.accessToken || response.data.accessToken;
        const refreshToken = response.data.data?.refreshToken || response.data.refreshToken;
        
        // 토큰 저장 및 상태 업데이트
        localStorage.setItem('token', accessToken);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
        
        setHospital(hospital || user);
        
        if (user && user.id) {
          localStorage.setItem('userId', user.id);
        }
        
        // 병원 ID 저장 (병원 정보가 있는 경우)
        if (hospital && hospital.id) {
          localStorage.setItem('hospitalId', hospital.id);
        }
        
        setIsAuthenticated(true);
      }
      
      // 사용자 정보만 반환 (로그인 상태 변경 없이)
      return { user, hospital };
    } catch (err: any) {
      setError(err.message || '회원가입에 실패했습니다.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 로그아웃 함수
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('hospitalId');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');  // ✅ user 정보도 제거
    setHospital(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  // 프로필 업데이트 함수
  const updateProfile = async (hospitalData: any) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 백엔드 연결이 없으므로 임시 처리
      if (isAuthenticated) {
        const updatedHospital = { ...hospital, ...hospitalData };
        setHospital(updatedHospital as Hospital);
      } else {
        throw new Error('먼저 로그인해야 합니다.');
      }
      
      // 실제 API 호출 (백엔드 연결 시 활성화)
      // const data = await authApi.updateProfile(hospitalData);
      // setHospital(data.hospital);
    } catch (err: any) {
      setError(err.message || '프로필 업데이트에 실패했습니다.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 변경 함수
  const changePassword = async (currentPassword: string, newPassword: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 백엔드 연결이 없으므로 임시 에러 처리
      throw new Error('현재 비밀번호 변경 기능을 사용할 수 없습니다.');
      
      // 실제 API 호출 (백엔드 연결 시 활성화)
      // await authApi.changePassword(currentPassword, newPassword);
    } catch (err: any) {
      setError(err.message || '비밀번호 변경에 실패했습니다.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 컨텍스트 값
  const value = {
    hospital,
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 커스텀 훅 생성
export const useAuth = () => useContext(AuthContext);

export default AuthContext; 